const MAX_WIDTH = 20;

export const GENERIC_ALERT_NAME = "A Wellness Check Required";

export const CRITICAL_WAITING_MESSAGE =
  "A Wellness Check Required is waiting. Acknowledge the notice to view it.";

export function truncateLocation(tier1, tier2, tier3) {
  if (tier1 && tier2 && tier3) {
    const full = `${tier1} - ${tier2} - ${tier3}`;
    if (full.length <= MAX_WIDTH) return full;
    const two = `${tier2} - ${tier3}`;
    if (two.length <= MAX_WIDTH) return two;
    if (tier3.length <= MAX_WIDTH) return tier3;
  } else if (tier1 && tier2) {
    const two = `${tier1} - ${tier2}`;
    if (two.length <= MAX_WIDTH) return two;
    if (tier2.length <= MAX_WIDTH) return tier2;
  } else if (tier1 && tier1.length <= MAX_WIDTH) {
    return tier1;
  }
  return "--";
}

export function getToastPresentation(toast, userData) {
  const alertStates = userData?.entityAssets?.alertStates;
  const stateIndex =
    alertStates?.findIndex(
      (state) => state.priorityValue === toast?.documentInfo?.priorityValue,
    ) ?? -1;

  const fallbackState = alertStates?.[alertStates?.length - 1];

  const colorStyle =
    stateIndex !== -1
      ? { color: alertStates[stateIndex]?.wellnessColor }
      : { color: fallbackState?.lightThemeCardColor };

  const priorityDescription =
    stateIndex !== -1
      ? alertStates[stateIndex]?.wellnessPriorityDescription
      : fallbackState?.priorityDescription;

  const showBelowSubjectName = userData?.entityInformation?.entityPreference
    ?.showJmsId
    ? `ID: ${toast?.documentInfo?.jmsId || "---------"}`
    : userData?.entityInformation?.entityPreference?.showBookingId
      ? `ID: ${toast?.documentInfo?.bookingId || "---------"}`
      : "";

  const doc = toast?.documentInfo;
  const standardLocation = doc
    ? truncateLocation(doc.tier1Location, doc.tier2Location, doc.tier3Location)
    : "--";

  const lorawanLocation = doc?.loRaWANLastLocation
    ? truncateLocation(
        doc.loRaWANLastLocation.tier1,
        doc.loRaWANLastLocation.tier2,
        doc.loRaWANLastLocation.tier3,
      )
    : "--";

  const truncatedLocationType =
    doc?.connectionType === "lorawan" ? lorawanLocation : standardLocation;

  const showJmsOrBookingId = userData?.entityInformation?.entityPreference
    ?.showJmsId
    ? doc?.jmsId
      ? `ID: ${doc.jmsId}`
      : ""
    : userData?.entityInformation?.entityPreference?.showBookingId
      ? doc?.bookingId
        ? `ID: ${doc.bookingId}`
        : ""
      : "";

  const displayName =
    doc?.firstName && doc?.lastName
      ? `${doc.firstName} ${doc.lastName}`
      : doc?.firstName || doc?.lastName || "--";

  const imageUrl = doc?.imageUrl || null;

  const fallbackImageSrc = doc?.userId
    ? "/svgs/Light_Checkout_Sensor_PFP.svg"
    : "/svgs/MonitoredSubjectCard/user-circle.svg";

  return {
    colorStyle,
    priorityDescription,
    showBelowSubjectName,
    truncatedLocationType,
    showJmsOrBookingId,
    displayName,
    imageUrl,
    fallbackImageSrc,
  };
}

export function getAlertActionLabels(userData) {
  const modal =
    userData?.entityInformation?.alertPreferences?.highWellnessModal;
  return {
    responding: modal?.respondingButton?.text || "Responding",
    occupied: modal?.occupiedButton?.text || "Occupied",
    occupiedHidden: !!modal?.occupiedButton?.hide,
  };
}

export function getAlertActionLabel(action, userData) {
  const labels = getAlertActionLabels(userData);
  return action === "Busy" ? labels.occupied : labels.responding;
}

export function criticalAlertName(toast) {
  try {
    const doc = toast?.documentInfo;
    const name =
      doc?.firstName && doc?.lastName
        ? `${doc.firstName} ${doc.lastName}`
        : doc?.firstName || doc?.lastName;
    return typeof name === "string" && name.trim()
      ? name.trim()
      : GENERIC_ALERT_NAME;
  } catch (e) {
    return GENERIC_ALERT_NAME;
  }
}

export function describeCriticalAlert(toast, userData) {
  try {
    const presentation = getToastPresentation(toast, userData);
    const parts = [criticalAlertName(toast)];
    if (presentation.showJmsOrBookingId) {
      parts.push(presentation.showJmsOrBookingId);
    }
    if (presentation.priorityDescription) {
      parts.push(String(presentation.priorityDescription));
    }
    if (
      presentation.truncatedLocationType &&
      presentation.truncatedLocationType !== "--"
    ) {
      parts.push(`location ${presentation.truncatedLocationType}`);
    }
    return `${parts.join(", ")}.`;
  } catch (e) {
    return `${GENERIC_ALERT_NAME}.`;
  }
}

export function alertActionErrorMessage(error, label) {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const still = "The alert is still open.";
  if (status === 404 && body === "Subject Not Found.") {
    return `${label} was not recorded: this subject is no longer being monitored. ${still}`;
  }
  if (status === 409) {
    return `${label} was not recorded because the alert was being updated at the same time. Select ${label} again. ${still}`;
  }
  if (status === 400 || status === 401 || status === 403) {
    return `${label} was not recorded: your sign-in could not be confirmed. Select ${label} again. If it keeps failing, sign out and back in. ${still}`;
  }
  if (!error?.response) {
    return `${label} was not recorded: OverWatch could not be reached. Check the connection, then select ${label} again. ${still}`;
  }
  return `${label} was not recorded. Select ${label} again. ${still}`;
}

export function describeAlertRemoval(
  toast,
  { resolvedAction, dismissed, subjects, userId, actionState, userData } = {},
) {
  try {
    const name = criticalAlertName(toast);
    const labels = getAlertActionLabels(userData);
    if (resolvedAction === "Busy") {
      return `${labels.occupied} recorded for ${name}. It stays in your Wellness Check Required count.`;
    }
    if (resolvedAction) return `${labels.responding} recorded for ${name}.`;
    if (dismissed) return `Wellness Check Required for ${name} dismissed.`;
    const subject = subjects?.find((s) => s?.id === toast?.documentInfo?.id);
    if (!subject)
      return `Wellness Check Required for ${name} cleared: no longer being monitored.`;
    if (subject.wellnessCategory !== "High") {
      return `Wellness Check Required for ${name} cleared: no longer critical.`;
    }
    const respondedHere = subject.officersRespondedToCritical?.some(
      (officer) => officer?.user === userId,
    );
    if (respondedHere && actionState?.action === "Responded") {
      return `${labels.responding} recorded for ${name}.`;
    }
    if (respondedHere) {
      return `Wellness Check Required for ${name} cleared: your response was recorded on another screen.`;
    }
    return `Wellness Check Required for ${name} cleared.`;
  } catch (e) {
    return "A Wellness Check Required cleared.";
  }
}

export function countLabel(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

const spokenPart = (value) => {
  if (typeof value !== "string" && typeof value !== "number") return "";
  const text = String(value).trim();
  return text === "--" ? "" : text;
};

export function describeStatusToast(toast, userData) {
  try {
    const { displayName, priorityDescription } = getToastPresentation(
      toast,
      userData,
    );
    const parts = [displayName, priorityDescription];
    if (parts.some((part) => part !== null && typeof part === "object")) {
      return "";
    }
    const sentence = parts.map(spokenPart).filter(Boolean).join(", ");
    if (!sentence) return "";
    return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
  } catch (e) {
    return "";
  }
}

export function describeStatusToasts(toasts, userData) {
  return (toasts || [])
    .map((toast) => describeStatusToast(toast, userData))
    .filter(Boolean)
    .join(" ");
}
